import type { RestClient } from "./restClient";

/** The caller's own short-term memory document (USER.md).
 *
 *  `status` deliberately separates "we could not read it" from "there is
 *  nothing stored": rendering an outage as an empty memory would tell the user
 *  Artie remembers nothing about them, which may be false.
 *    ok             — a document exists
 *    empty          — read succeeded, no document yet
 *    unavailable    — the read failed (outage, 403, throttling); retry may help
 *    not_configured — no memory store provisioned here; retrying cannot help
 */
export interface ShortTermMemory {
  status: "ok" | "empty" | "unavailable" | "not_configured";
  profile: { bullets: string[]; updated: string | null };
  recent_activity: {
    prose: string;
    open_items: string[];
    updated: string | null;
    /** True once the user has replaced this block by hand, which is what stops
     *  the summariser overwriting it. Drives the "Let Artie manage this again"
     *  control — offering that when Artie still owns the block would be a lie. */
    user_edited: boolean;
  };
  /** The verbatim USER.md, bookkeeping comments included. Null when absent. */
  raw: string | null;
  raw_truncated: boolean;
  /** Echoed back when saving, so a chat turn that rewrote the document while
   *  the user was editing produces a 409 instead of losing their work. */
  version: string;
}

/** Give up rather than spin indefinitely. A hung request is worse than a failed
 *  one: the dialog would keep showing a spinner and look like it is still
 *  working, when in fact nothing is ever going to arrive. */
export const MEMORY_FETCH_TIMEOUT_MS = 12_000;

/** Mirror the server-side caps so the UI bounds input rather than surfacing a 400. */
export const MAX_PROSE_CHARS = 500;
export const MAX_OPEN_ITEMS = 3;

export class ShortTermMemoryApi {
  constructor(private client: RestClient) {}

  /** Fetch the current document. Pass a signal to cancel on unmount; otherwise
   *  a timeout signal is supplied so the request cannot hang forever.
   *
   *  Uses `json()` rather than `get()` because only the former forwards a
   *  RequestInit (and therefore an AbortSignal) to fetch.
   */
  get(signal?: AbortSignal): Promise<ShortTermMemory> {
    return this.client.json<ShortTermMemory>("/v1/memory/short-term", {
      method: "GET",
      signal: signal ?? AbortSignal.timeout(MEMORY_FETCH_TIMEOUT_MS),
    });
  }

  /** Replace the Recent Activity block with the user's own text.
   *
   *  `version` must be the one from the last get(); a mismatch throws (409),
   *  which the caller should surface while keeping the draft.
   */
  saveActivity(args: {
    prose: string;
    open_items: string[];
    version: string;
  }): Promise<{ status: string }> {
    return this.client.post("/v1/memory/short-term/activity", args);
  }

  /** Hand the block back to Artie: clears the user-edited marker so the next
   *  summarisation regenerates it. The previous machine-written text is not
   *  stored anywhere — it does not need to be, since it can be regenerated. */
  clearActivityEdit(args: {
    prose: string;
    open_items: string[];
    version: string;
  }): Promise<{ status: string }> {
    // Sends the CURRENT text, not blanks. Releasing ownership changes who may
    // rewrite the block, not whether it has content — clearing it would leave
    // the section empty until some future turn regenerated it, which may never
    // come if the user stops chatting.
    return this.client.post("/v1/memory/short-term/activity", {
      ...args,
      auto: true,
    });
  }
}
